import { Injectable } from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { catchError } from 'rxjs/operators';
import { ProductDetail, GetFullStockDetails, SetStockSpec } from '../models/product-detail.model';
import { Image, Pdf, Link, Option, Specification, Specification_Extended } from '../models/product-view.model';
import { AuthService } from './auth.service';
import { SpecificationModel } from '../models/specification.model';
import { ProductPricingModel } from '../models/product-pricing.model';
import { ApiResponse } from '../models/api-response.model';
import { SkuList } from '../models/sku-list.model';

@Injectable()
export class ProductService {

  baseUrl = 'api/product';

  constructor(private http: HttpClient,
    private authService: AuthService
    ) { }

  getProductDetail(stockCode: string): Observable<ProductDetail> {
    return this.http.get<ProductDetail>(this.baseUrl + '/GetProductDetails?stockCode=' + stockCode)
      .pipe(catchError(this.authService.handleError));
  }

  GetFullStockProductDetails(stockCode: string): Observable<ProductDetail> {
    return this.http.get<ProductDetail>(this.baseUrl + '/GetFullStockProductDetails?stockCode=' + stockCode)
      .pipe(catchError(this.authService.handleError));
  }

  GetFullStockDetails(stockCode: string): Observable<GetFullStockDetails> {
    return this.http.get<GetFullStockDetails>(this.baseUrl + '/GetFullStockDetails?stockCode=' + stockCode)
      .pipe(catchError(this.authService.handleError));
  }

  getImageList(getFullStockDetails: GetFullStockDetails, stockCode: string): Observable<Image[]> {
    return this.http.post<Image[]>(this.baseUrl + '/getimagelist?stockCode=' + stockCode, getFullStockDetails)
      .pipe(catchError(this.authService.handleError));
  }

  getPdfList(getFullStockDetails: GetFullStockDetails, stockCode: string): Observable<Pdf[]> {
    return this.http.post<Pdf[]>(this.baseUrl + '/GetPdfList?stockCode=' + stockCode, getFullStockDetails)
      .pipe(catchError(this.authService.handleError));
  }

  getLinkList(getFullStockDetails: GetFullStockDetails, stockCode: string): Observable<Link[]> {
    return this.http.post<Link[]>(this.baseUrl + '/GetLinkList?stockCode=' + stockCode, getFullStockDetails)
      .pipe(catchError(this.authService.handleError));
  }

  GetSpecificationList(getFullStockDetails: GetFullStockDetails, stockCode: string): Observable<Specification[]> {
    return this.http.post<Specification[]>(this.baseUrl + '/GetSpecificationList?stockCode=' + stockCode, getFullStockDetails)
      .pipe(catchError(this.authService.handleError));
  }

  GetSpecificationExtendedList(getFullStockDetails: GetFullStockDetails, stockCode: string): Observable<Specification_Extended[]> {
    return this.http.post<Specification_Extended[]>(this.baseUrl + '/GetSpecificationExtendedList?stockCode=' + stockCode, getFullStockDetails)
      .pipe(catchError(this.authService.handleError));
  }

  GetSpecification(stockCodeList: string[]): Observable<SpecificationModel> {
    return this.http.post<SpecificationModel>(this.baseUrl + '/GetSpecification' , stockCodeList)
      .pipe(catchError(this.authService.handleError));
  }

  GetHasNotes(getFullStockDetails: GetFullStockDetails, stockCode: string): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + '/GetHasNotes?stockCode=' + stockCode, getFullStockDetails)
      .pipe(catchError(this.authService.handleError));
  }

  SetStockSpec(stockCode: string, termsDate: string, termsTime: string): Observable<SetStockSpec> {
    return this.http.get<SetStockSpec>(this.baseUrl + `/SetStockSpec?stockCode=${stockCode}&termsDate=${termsDate}&termsTime=${termsTime}`)
    .pipe(catchError(this.authService.handleError));
  }

  SendProductEnquiryEmail(enquiryText: string, contactEmail: string, stockCode: string, stkBrand: string, stkDescription: string): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(this.baseUrl + `/SendProductEnquiryEmail?enquiryText=${enquiryText}&contactEmail=${contactEmail}&stockCode=${stockCode}&stkBrand=${stkBrand}&stkDescription=${stkDescription}`)
    .pipe(catchError(this.authService.handleError));
  }

  // List of new APIs
  GetProductPricing(stockCodeList: string[]): Observable<ProductPricingModel> {
    return this.http.post<ProductPricingModel>(this.baseUrl + '/GetProductPricing', stockCodeList)
    .pipe(catchError(this.authService.handleError));
  }

  GetBrandList() {
    return this.http.get(this.baseUrl + '/GetProntoBrandList')
        .pipe(catchError(this.authService.handleError));
  }

  GetProductsByStockGroup(stkGroupFrom: string, stkGroupTo: string): Observable<SkuList[]> {
    return this.http.get<SkuList[]>(this.baseUrl + '/GetSkuList?stkGroupFrom=' + stkGroupFrom + '&stkGroupTo=' + stkGroupTo)
    .pipe(catchError(this.authService.handleError));
  }

  RequestNewProductSpecs(stockCodeList: string[]): Observable<ApiResponse>{
    return this.http.post<ApiResponse>(this.baseUrl + '/RequestProductSpecification', stockCodeList)
      .pipe(catchError(this.authService.handleError));
  }

  GetDellPowerQuotes(sku: string): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.baseUrl + '/GetDellPowerQuotes', JSON.stringify(sku), {
      headers: new HttpHeaders().set('Content-Type', 'application/json')
    }).pipe(catchError(this.authService.handleError));
  }
}
